import type { JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Plan 07-02 D-70: shown before the actual PUT /api/roles/:id when the user
 * is about to remove permissions from a role with assigned users. Lets the
 * user see how many users will be impacted before confirming.
 */
export interface RemovePermissionsConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly removingCodes: readonly string[];
  readonly affectedUserCount: number;
  readonly onConfirm: () => void;
  readonly submitting?: boolean;
}

export function RemovePermissionsConfirmDialog({
  open,
  onOpenChange,
  removingCodes,
  affectedUserCount,
  onConfirm,
  submitting = false,
}: RemovePermissionsConfirmDialogProps): JSX.Element {
  const codeList = removingCodes.join(', ');
  const userWord = affectedUserCount === 1 ? 'user' : 'users';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="remove-permissions-dialog"
        aria-describedby="remove-permissions-description"
      >
        <DialogHeader>
          <DialogTitle>Remove permissions?</DialogTitle>
          <DialogDescription id="remove-permissions-description">
            Removing the following permissions will affect {affectedUserCount} {userWord} assigned
            to this role.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/30 p-3" data-testid="remove-permissions-codes">
          <code className="font-mono text-xs break-all">{codeList}</code>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={submitting}
            data-testid="remove-permissions-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={submitting}
            data-testid="remove-permissions-confirm"
          >
            {submitting ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
